import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faAlien,
  faAnchor,
  faAxe,
  faAxeBattle,
  faBadgeSheriff,
  faBatteryFull,
  faBird,
  faBolt,
  faBomb,
  faBowArrow,
  faBoxHeart,
  faBoxOpen,
  faBullseyeArrow,
  faCactus,
  faCampfire,
  faCandleHolder,
  faCastle,
  faChartMixed,
  faCode,
  faCoins,
  faComet,
  faCompactDisc,
  faCompass,
  faCow,
  faCrown,
  faCrystalBall,
  faDagger,
  faDna,
  faDragon,
  faEclipse,
  faFire,
  faFireFlameCurved,
  faFireFlameSimple,
  faFlagSwallowtail,
  faFlask,
  faGalaxy,
  faGear,
  faGlobe,
  faGun,
  faHatCowboy,
  faHatCowboySide,
  faHatWizard,
  faHorse,
  faHorseHead,
  faHorseSaddle,
  faLaptopCode,
  faLasso,
  faLock,
  faLockKeyhole,
  faMailbox,
  faMapLocationDot,
  faMeteor,
  faMicrochip,
  faMoon,
  faMoonStars,
  faNewspaper,
  faOctopus,
  faPlanetMoon,
  faPlanetRinged,
  faRadar,
  faRocket,
  faRocketLaunch,
  faSackDollar,
  faSatellite,
  faSatelliteDish,
  faScaleBalanced,
  faScrewdriverWrench,
  faScroll,
  faScrollOld,
  faShield,
  faShieldCross,
  faShieldHalved,
  faShip,
  faSignal,
  faSignalStream,
  faSkullCrossbones,
  faSliders,
  faSolarSystem,
  faSparkles,
  faStar,
  faSun,
  faSunrise,
  faSword,
  faSwords,
  faTelescope,
  faToolbox,
  faTowerBroadcast,
  faTrafficLight,
  faTrain,
  faTrainTrack,
  faTreasureChest,
  faTree,
  faTrumpet,
  faUfo,
  faUserAstronaut,
  faUserSecret,
  faWandMagicSparkles,
  faWave,
  faWhiskeyGlassIce,
  faWrench,
} from '@fortawesome/pro-solid-svg-icons';
import { Icon } from 'corso-admin-ui';
import { template } from 'corso-web-lib';
import { useMemo, useState } from 'react';
import { useIsCorsoCxAgent } from '~/hooks/useIsCorsoAdmin';
import { useIsConciergeWidget } from '~/hooks/widgetHooks';
import { useUser } from '~/providers/UserProvider';

export const messageTemplates = [
  {
    icon: faTreasureChest,
    message: `{{name}}, reclaim that lost package like buried treasure.`,
  },
  { icon: faBoxOpen, message: `"Stolen?" Not on yer watch, {{name}}.` },
  {
    icon: faAnchor,
    message: `{{name}}, steady the ship—another refund storm brews.`,
  },
  {
    icon: faMapLocationDot,
    message: `Chart a course for replacements, {{name}}.`,
  },
  { icon: faBomb, message: `{{name}}, obliterate WISMO with pirate ferocity.` },
  {
    icon: faSkullCrossbones,
    message: `Hoist the sails, {{name}}—refunds await.`,
  },
  {
    icon: faRocket,
    message: `{{name}}, send that reorder faster than a cannonball.`,
  },
  { icon: faBird, message: `{{name}}, speak softly. Refund swiftly.` },
  {
    icon: faSwords,
    message: `{{name}}, duel every damaged shipment into submission.`,
  },
  {
    icon: faCoins,
    message: `A lost parcel? {{name}} calls that easy plunder.`,
  },
  {
    icon: faWave,
    message: `{{name}}, ride the waves of chaos ‘til every customer smiles.`,
  },
  {
    icon: faMailbox,
    message: `{{name}}, replace it like you're rescuin' treasure from the sea.`,
  },
  { icon: faCompass, message: `{{name}}, guide these refunds true north.` },
  { icon: faLock, message: `Stolen packages fear yer name, {{name}}.` },
  {
    icon: faShip,
    message: `{{name}}, run your reorder ops like a ghost ship with WiFi.`,
  },
  {
    icon: faOctopus,
    message: `{{name}}, wrestle the Kraken of logistics—and win.`,
  },
  {
    icon: faFire,
    message: `{{name}}, turn customer despair into delight like pirate alchemy.`,
  },
  {
    icon: faScroll,
    message: `{{name}}, write refund decrees with ruthless elegance.`,
  },
  { icon: faAxe, message: `{{name}}, chop delays in half like driftwood.` },
  {
    icon: faFlagSwallowtail,
    message: `{{name}}, rule these lost-item seas with iron metrics.`,
  },
  {
    icon: faRocketLaunch,
    message: `{{name}}, launch that reorder into the stratosphere.`,
  },
  {
    icon: faSatellite,
    message: `{{name}}, track that lost package like deep-space telemetry.`,
  },
  {
    icon: faGalaxy,
    message: `{{name}}, bend spacetime to deliver refunds faster.`,
  },
  {
    icon: faUfo,
    message: `{{name}}, abduct that WISMO before it abducts your day.`,
  },
  {
    icon: faPlanetRinged,
    message: `{{name}}, orbit excellence—refunds handled flawlessly.`,
  },
  {
    icon: faComet,
    message: `{{name}}, streak across the dashboard like a shipping comet.`,
  },
  {
    icon: faUserAstronaut,
    message: `{{name}}, suit up—another damaged parcel needs rescue.`,
  },
  {
    icon: faSatelliteDish,
    message: `{{name}}, lock on to that replacement like a guidance system.`,
  },
  {
    icon: faMeteor,
    message: `{{name}}, vaporize shipping delays on re-entry.`,
  },
  {
    icon: faFlask,
    message: `{{name}}, engineer refund perfection in your command module.`,
  },
  {
    icon: faStar,
    message: `{{name}}, shine brighter than expedited shipping.`,
  },
  {
    icon: faPlanetMoon,
    message: `{{name}}, your refund rate defies known physics.`,
  },
  {
    icon: faGear,
    message: `{{name}}, recalibrate the cosmos—another lost item inbound.`,
  },
  {
    icon: faTowerBroadcast,
    message: `{{name}}, transmit pure customer delight across the void.`,
  },
  {
    icon: faEclipse,
    message: `{{name}}, eclipse every stolen package with instant action.`,
  },
  {
    icon: faAlien,
    message: `{{name}}, deliver reorders with extraterrestrial precision.`,
  },
  {
    icon: faSolarSystem,
    message: `{{name}}, treat every lost shipment like a rogue asteroid.`,
  },
  {
    icon: faFireFlameCurved,
    message: `{{name}}, ignite thrusters—refund storms ahead.`,
  },
  {
    icon: faTelescope,
    message: `{{name}}, your tracking skills belong at NASA.`,
  },
  {
    icon: faMoonStars,
    message: `{{name}}, bring balance to the ecommerce galaxy.`,
  },
  {
    icon: faHatCowboy,
    message: `{{name}}, wrangle that lost package like a runaway steer.`,
  },
  {
    icon: faHorse,
    message: `{{name}}, chase down WISMO across the digital frontier.`,
  },
  {
    icon: faGun,
    message: `{{name}}, shoot down delays with dead-eye precision.`,
  },
  { icon: faSun, message: `{{name}}, tame the wild plains of customer chaos.` },
  {
    icon: faWhiskeyGlassIce,
    message: `{{name}}, take a swig and handle that refund clean.`,
  },
  {
    icon: faCactus,
    message: `{{name}}, stand tall—another stolen package needs justice.`,
  },
  {
    icon: faTrain,
    message: `{{name}}, drive replacements faster than a freight train.`,
  },
  {
    icon: faSackDollar,
    message: `{{name}}, protect each parcel like gold in a saddlebag.`,
  },
  {
    icon: faLasso,
    message: `{{name}}, lasso that reorder with cowboy efficiency.`,
  },
  {
    icon: faNewspaper,
    message: `{{name}}, your refund logs read like frontier legend.`,
  },
  {
    icon: faHatCowboySide,
    message: `{{name}}, rustle up delight for every customer.`,
  },
  {
    icon: faCow,
    message: `{{name}}, charge through damaged shipments head-on.`,
  },
  {
    icon: faSunrise,
    message: `{{name}}, sunrise means fresh claims on the horizon.`,
  },
  {
    icon: faWrench,
    message: `{{name}}, fix that issue faster than a saloon brawl ends.`,
  },
  {
    icon: faBullseyeArrow,
    message: `{{name}}, your accuracy would impress any sharpshooter.`,
  },
  {
    icon: faBadgeSheriff,
    message: `{{name}}, bring law and order to the dusty trails of logistics.`,
  },
  { icon: faTree, message: `{{name}}, chop through delays like firewood.` },
  {
    icon: faHorseHead,
    message: `{{name}}, ride hard into the land of zero WISMO.`,
  },
  {
    icon: faTrainTrack,
    message: `{{name}}, stay on track—refund rails are smooth today.`,
  },
  {
    icon: faFireFlameSimple,
    message: `{{name}}, blaze a trail through customer issues like folklore.`,
  },
  {
    icon: faCompactDisc,
    message: `{{name}}, reroute that lost parcel through the neon grid.`,
  },
  {
    icon: faScrewdriverWrench,
    message: `{{name}}, patch the system—WISMO detected in the subnet.`,
  },
  {
    icon: faGlobe,
    message: `{{name}}, your refund velocity breaks the firewall.`,
  },
  {
    icon: faUserSecret,
    message: `{{name}}, execute refund protocol: zero hesitation.`,
  },
  {
    icon: faBolt,
    message: `{{name}}, override delays with cybernetic precision.`,
  },
  {
    icon: faDna,
    message: `{{name}}, splice customer delight into the supply chain.`,
  },
  {
    icon: faRadar,
    message: `{{name}}, track that stolen package across the dark net.`,
  },
  {
    icon: faLaptopCode,
    message: `{{name}}, hack the chaos—restore order to the dashboard.`,
  },
  {
    icon: faBatteryFull,
    message: `{{name}}, your efficiency is fully charged and overclocked.`,
  },
  {
    icon: faSliders,
    message: `{{name}}, tune the ops machine like a street-samurai.`,
  },
  {
    icon: faSignalStream,
    message: `{{name}}, broadcast a replacement at lightspeed.`,
  },
  {
    icon: faMicrochip,
    message: `{{name}}, glitch every damaged parcel out of existence.`,
  },
  {
    icon: faCode,
    message: `{{name}}, decrypt shipping anomalies with elegance.`,
  },
  {
    icon: faToolbox,
    message: `{{name}}, mod the refund pipeline like a chrome-jockey.`,
  },
  {
    icon: faMoon,
    message: `{{name}}, run a silent op—fix issues before they spark.`,
  },
  {
    icon: faChartMixed,
    message: `{{name}}, optimize metrics like a nocturnal netrunner.`,
  },
  {
    icon: faSparkles,
    message: `{{name}}, update customer fate with one keystroke.`,
  },
  {
    icon: faTrafficLight,
    message: `{{name}}, turn red alerts green with cyber calm.`,
  },
  {
    icon: faLockKeyhole,
    message: `{{name}}, lock down lost shipments like top-tier encryption.`,
  },
  {
    icon: faSignal,
    message: `{{name}}, amplify your signal—another case resolved.`,
  },
  {
    icon: faShield,
    message: `{{name}}, defend the kingdom from lost parcels.`,
  },
  {
    icon: faScrollOld,
    message: `{{name}}, issue a royal refund decree at once.`,
  },
  {
    icon: faSword,
    message: `{{name}}, vanquish WISMO as if it were a foul beast.`,
  },
  {
    icon: faCastle,
    message: `{{name}}, guard every shipment like sacred treasure.`,
  },
  {
    icon: faHatWizard,
    message: `{{name}}, conjure replacements with arcane speed.`,
  },
  {
    icon: faDragon,
    message: `{{name}}, slay the dragon of damaged inventory.`,
  },
  {
    icon: faCrown,
    message: `{{name}}, rule the reorder realm with steady mercy.`,
  },
  {
    icon: faCandleHolder,
    message: `{{name}}, illuminate the darkest tracking mysteries.`,
  },
  {
    icon: faAxeBattle,
    message: `{{name}}, split delays like firewood for the feast.`,
  },
  {
    icon: faBowArrow,
    message: `{{name}}, strike each issue with archer’s precision.`,
  },
  {
    icon: faWandMagicSparkles,
    message: `{{name}}, enchant your workflow with unseen efficiency.`,
  },
  {
    icon: faBoxHeart,
    message: `{{name}}, bless each parcel with safe passage.`,
  },
  {
    icon: faScaleBalanced,
    message: `{{name}}, restore order when shipping chaos reigns.`,
  },
  {
    icon: faCrystalBall,
    message: `{{name}}, foresee the customer’s need before they ask.`,
  },
  {
    icon: faHorseSaddle,
    message: `{{name}}, ride swiftly to rescue damaged goods.`,
  },
  {
    icon: faDagger,
    message: `{{name}}, cut through refund queues with knightly grace.`,
  },
  {
    icon: faCampfire,
    message: `{{name}}, scorch the earth of all lost-item foes.`,
  },
  {
    icon: faTrumpet,
    message: `{{name}}, herald smooth operations across the land.`,
  },
  {
    icon: faShieldHalved,
    message: `{{name}}, stand firm—another claim approaches.`,
  },
  {
    icon: faShieldCross,
    message: `{{name}}, shine like a paladin of perfect deliveries.`,
  },
] as const satisfies readonly { icon: IconDefinition; message: string }[];

export default function CorsoConciergeMotivator() {
  const [dismissed, setDismissed] = useState(false);
  const isCorsoConcierge = useIsConciergeWidget();
  const isCorsoCxAgent = useIsCorsoCxAgent();
  const user = useUser();

  const motivation = useMemo(() => {
    const messageTemplate =
      messageTemplates[Math.floor(Math.random() * messageTemplates.length)];
    if (!messageTemplate) throw new Error('No Message Template'); // * should never happen, but guards against it
    return {
      icon: messageTemplate.icon,
      message: template(messageTemplate.message, { name: user.firstName }),
    };
  }, [user.firstName]);

  if (!isCorsoConcierge || !isCorsoCxAgent) return null;

  return (
    <button
      type="button"
      className="mt-2 flex w-full items-center gap-1.5 text-left text-xs text-pretty opacity-80"
      onClick={() => setDismissed(true)}
      hidden={dismissed}
    >
      <Icon icon={motivation.icon} className="size-3" /> {motivation.message}
      <span className="ml-auto">
        <Icon icon="close" />
        <span className="sr-only">Dismiss</span>
      </span>
    </button>
  );
}
